import { useState, useEffect } from 'react';
import Navigation from '../feature/Navigation';
import Footer from '../../pages/home/components/Footer';
import PriceCalculator from '../../pages/home/components/PriceCalculator';

interface CityPageProps {
  cityName: string;
  cityNameFormatted: string;
  schipholPrice: number;
  rotterdamPrice: number;
  eindhovenPrice: number;
}

export default function CityAirportTemplate({ 
  cityName, 
  cityNameFormatted,
  schipholPrice,
  rotterdamPrice,
  eindhovenPrice,
  airport 
}: CityPageProps & { airport: 'schiphol' | 'rotterdam-airport' | 'eindhoven-airport' }) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const airportInfo = {
    'schiphol': {
      name: 'Schiphol',
      fullName: 'Amsterdam Airport Schiphol',
      price: schipholPrice,
      duration: '30-40',
      description: 'Schiphol Airport is het grootste vliegveld van Nederland en een van de drukste luchthavens van Europa'
    },
    'rotterdam-airport': {
      name: 'Rotterdam Airport',
      fullName: 'Rotterdam The Hague Airport',
      price: rotterdamPrice,
      duration: '45-55',
      description: 'Rotterdam Airport is een compact en overzichtelijk vliegveld, ideaal voor Europese bestemmingen'
    },
    'eindhoven-airport': {
      name: 'Eindhoven Airport',
      fullName: 'Eindhoven Airport',
      price: eindhovenPrice,
      duration: '60-75',
      description: 'Eindhoven Airport is het op één na grootste vliegveld van Nederland, populair bij budget airlines'
    }
  };

  const info = airportInfo[airport];

  const faqs = [
    {
      question: `Wat kost een taxi van ${cityNameFormatted} naar ${info.name}?`,
      answer: `De prijs voor een taxi van ${cityNameFormatted} naar ${info.fullName} is €${info.price.toFixed(2)} all-in. Dit is een vaste prijs zonder verborgen kosten of nachttoeslagen.`
    },
    {
      question: `Hoe lang duurt de rit van ${cityNameFormatted} naar ${info.name}?`,
      answer: `De reistijd van ${cityNameFormatted} naar ${info.fullName} is gemiddeld ${info.duration} minuten, afhankelijk van het verkeer en het tijdstip.`
    },
    {
      question: 'Zijn er nachttoeslagen?',
      answer: 'Nee, wij rekenen geen nachttoeslagen. Onze vaste prijzen gelden 24/7, ook in het weekend en op feestdagen.'
    },
    {
      question: `Kan ik ook vanaf ${info.name} naar ${cityNameFormatted} worden opgehaald?`,
      answer: `Ja, wij verzorgen ook ritten vanaf ${info.fullName} naar ${cityNameFormatted}. Onze chauffeur wacht op u in de aankomsthal en volgt uw vlucht automatisch.`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/8eb87f131af201006da945d8b4ad5872.jpg')] bg-cover bg-center"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Taxi {cityNameFormatted} {info.name} – Vaste Prijs €{info.price.toFixed(2)}
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Betrouwbare taxi van {cityNameFormatted} naar {info.fullName} met vaste all-in prijzen. Geen verborgen kosten, 24/7 beschikbaar. Boek nu uw airport taxi.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#calculator" className="px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap">
                Prijs Berekenen
              </a>
              <a href="tel:+31658824429" className="px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>Bel Direct
              </a>
              <a href="https://wa.me/31658824429" className="px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <div id="calculator">
        <PriceCalculator />
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Taxi {cityNameFormatted} naar {info.name}: Uw Betrouwbare Partner</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Vliegt u vanaf <strong>{info.fullName}</strong> en vertrekt u vanuit <strong>{cityNameFormatted}</strong>? Utrecht Airport Taxi brengt u comfortabel en op tijd naar het vliegveld. Met onze vaste all-in prijs van <strong>€{info.price.toFixed(2)}</strong> weet u vooraf exact wat u betaalt. Geen verrassingen, geen nachttoeslagen, geen verborgen kosten.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              {info.description}. Vanuit {cityNameFormatted} is {info.name} goed bereikbaar. De reistijd is gemiddeld {info.duration} minuten, afhankelijk van het verkeer en het tijdstip van de dag. Onze ervaren chauffeurs kennen de beste routes en zorgen dat u op tijd aankomt voor uw vlucht.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-8">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Vaste Prijs Garantie</h3>
              <p className="text-slate-700 mb-0">
                Taxi {cityNameFormatted} naar {info.name}: <strong>€{info.price.toFixed(2)} all-in</strong><br />
                24/7 dezelfde prijs • Geen nachttoeslagen • Inclusief vluchttracking
              </p>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Waarom Kiezen voor Utrecht Airport Taxi?</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Als lokale taxiservice uit de regio Utrecht hebben wij ruime ervaring met luchthaventritten vanuit {cityNameFormatted}. Onze chauffeurs wonen en werken in de regio, kennen alle routes en weten precies hoe zij u snel en comfortabel naar {info.name} brengen. Wij zijn geen landelijke taxicentrale, maar een lokaal bedrijf dat dicht bij u staat.
            </p>

            <ul className="list-disc pl-6 mb-6 text-lg text-slate-700 space-y-2">
              <li>Vaste all-in prijzen zonder verrassingen</li>
              <li>Geen nachttoeslagen of weekendtoeslagen</li>
              <li>Professionele lokale chauffeurs</li>
              <li>Moderne, goed onderhouden voertuigen</li>
              <li>Vluchttracking bij ophalen vanaf vliegveld</li>
              <li>24/7 bereikbaar voor boekingen</li>
              <li>Betalen met iDEAL, pin, creditcard of contant</li>
            </ul>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Ook vanaf {info.name} naar {cityNameFormatted}</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Komt u aan op {info.fullName} en heeft u een <strong>taxi naar {cityNameFormatted}</strong> nodig? Wij verzorgen ook ritten vanaf het vliegveld naar {cityNameFormatted} en omgeving. Onze chauffeur wacht op u in de aankomsthal met een naambord, helpt met uw bagage en brengt u comfortabel naar huis.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Dankzij ons vluchttracking systeem volgen wij uw vlucht in real-time. Heeft uw vlucht vertraging? Geen probleem. Wij passen de ophaaltijd automatisch aan zonder extra kosten. U hoeft ons niet te bellen – wij regelen het voor u.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Hoe Werkt het Boeken?</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Het boeken van uw <strong>taxi {cityNameFormatted} {info.name}</strong> is eenvoudig. Gebruik onze online prijscalculator, vul uw gegevens in en u ziet direct de totaalprijs. Klik op "Reserveer Nu" en vul uw contactgegevens en vluchtnummer in. U ontvangt direct een bevestiging per e-mail.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Liever telefonisch boeken? Bel ons op <strong>06 5882 4429</strong>. Wij zijn 24/7 bereikbaar voor boekingen en vragen. U kunt ook via WhatsApp contact met ons opnemen. Wij adviseren om minimaal 24 uur van tevoren te reserveren voor gegarandeerde beschikbaarheid.
            </p>

            <div className="bg-slate-50 rounded-2xl p-8 my-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Direct Reserveren?</h3>
              <p className="text-lg text-slate-700 mb-6">
                Bereken uw prijs en boek direct online. Of bel ons voor persoonlijk advies.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#calculator" className="px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap">
                  Prijs Berekenen
                </a>
                <a href="tel:+31658824429" className="px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>06 5882 4429
                </a>
                <a href="https://wa.me/31658824429" className="px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                  <i className="ri-whatsapp-line mr-2"></i>WhatsApp
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-slate-900 mb-4 text-center">Veelgestelde Vragen</h2>
          <p className="text-xl text-slate-600 text-center mb-12">Alles over onze taxi service van {cityNameFormatted} naar {info.name}</p>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <summary className="text-lg font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-2xl text-amber-500"></i>
                </summary>
                <p className="mt-4 text-slate-700 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
